// CollectionIntelligence.tsx — the "what else can this app do" panel: 3 cards (Compare on the
// open web, Compare a link, Value & Rarity). Describe/Export stay where they're used — per-item
// and per-collection respectively. Results render as TABLES (source, link) with a narrated
// summary above — not a wall of prose — per Bogdan's explicit direction.
import { useState } from "react";
import { Search, Coins, Link as LinkIcon, Sparkles } from "lucide-react";
import { store, todayLocal, type Collection, type Item } from "./store";
import { compareCollection, compareLink, researchItem, BrainError } from "./brain";
import type { WebResult } from "./search";
import { BottomSheet } from "./App";

type Mode = "menu" | "compare-web" | "compare-link" | "value";

export function CollectionIntelligence({ collection, items, hasItems, offline, onClose }: {
  collection: Collection; items: Item[]; hasItems: boolean; offline: boolean; onClose: () => void;
}) {
  const [mode, setMode] = useState<Mode>("menu");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [text, setText] = useState<string | null>(null);
  const [sources, setSources] = useState<WebResult[]>([]);
  const [searched, setSearched] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [itemName, setItemName] = useState("");

  async function runCompareWeb() {
    setMode("compare-web"); setBusy(true); setErr(null); setText(null);
    try {
      const sig = await store.computeSignals(collection.id, todayLocal());
      const r = await compareCollection(sig);
      setText(r.text); setSources(r.sources); setSearched(r.searched);
    } catch (e) { setErr(e instanceof BrainError ? e.message : "That failed — please try again."); }
    finally { setBusy(false); }
  }

  async function runCompareLink() {
    if (!linkUrl.trim()) return;
    setBusy(true); setErr(null); setText(null);
    try {
      const sig = await store.computeSignals(collection.id, todayLocal());
      const r = await compareLink(sig, linkUrl.trim());
      setText(r.text); setSources(r.sources); setSearched(r.searched);
    } catch (e) { setErr(e instanceof BrainError ? e.message : "That failed — please try again."); }
    finally { setBusy(false); }
  }

  async function runValue() {
    if (!itemName.trim()) return;
    setBusy(true); setErr(null); setText(null);
    try {
      const r = await researchItem({ name: itemName.trim(), kind: collection.kind });
      setText(r.text); setSources(r.sources); setSearched(r.searched);
    } catch (e) { setErr(e instanceof BrainError ? e.message : "That failed — please try again."); }
    finally { setBusy(false); }
  }

  const CARDS: { key: Mode; icon: typeof Search; title: string; sub: string }[] = [
    { key: "compare-web", icon: Search, title: "Find similar collections", sub: "Search the open web for others like it" },
    { key: "compare-link", icon: LinkIcon, title: "Compare a link", sub: "Paste a collector's page or listing" },
    { key: "value", icon: Coins, title: "Value & rarity", sub: "Look up one item, cited" },
  ];

  return (
    <BottomSheet title="Collection Intelligence" onClose={onClose}>
      {offline ? (
        <div className="notice notice-warn"><span>You're offline — reconnect to use Collection Intelligence.</span></div>
      ) : !hasItems ? (
        <div className="muted" style={{ fontSize: "var(--fs-sm)" }}>Log a few items first, then these have something to work with.</div>
      ) : mode === "menu" ? (
        <div className="intel-grid">
          {CARDS.map((c) => {
            const Icon = c.icon;
            return (
              <button key={c.key} className="intel-card" onClick={() => (c.key === "compare-web" ? runCompareWeb() : setMode(c.key))}>
                <span className="ai-title"><Icon size={14} /> {c.title}</span>
                <span className="ai-sub">{c.sub}</span>
              </button>
            );
          })}
        </div>
      ) : (
        <div>
          {mode === "compare-link" && !text && !busy && (
            <div className="row" style={{ gap: "0.4rem", marginBottom: "0.8rem" }}>
              <input type="url" placeholder="https://…" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} style={{ flex: 1 }} />
              <button className="btn btn-primary btn-pill" onClick={runCompareLink} disabled={!linkUrl.trim()}>Compare</button>
            </div>
          )}
          {mode === "value" && !text && !busy && (
            <div className="row" style={{ gap: "0.4rem", marginBottom: "0.8rem" }}>
              <input type="text" placeholder="Item name" value={itemName} onChange={(e) => setItemName(e.target.value)} style={{ flex: 1 }} />
              <button className="btn btn-primary btn-pill" onClick={runValue} disabled={!itemName.trim()}>Look up</button>
            </div>
          )}

          {busy && <div className="muted row" style={{ gap: "0.4rem" }}><Sparkles size={12} /> Working…</div>}
          {err && !busy && <div className="notice notice-err">{err}</div>}

          {text && !busy && (
            <div className="fade-up">
              <p style={{ fontSize: "var(--fs-md)", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{text}</p>
              <div className="muted" style={{ fontSize: "var(--fs-xxs)", marginBottom: "0.5rem" }}>
                {searched ? "Based on a live web search." : "AI's own knowledge — no live search this time."}
              </div>
              {sources.length > 0 && (
                <table className="result-table">
                  <thead><tr><th>Source</th><th>Link</th></tr></thead>
                  <tbody>
                    {sources.map((s, i) => (
                      <tr key={i}>
                        <td>{s.title}</td>
                        <td><a href={s.url} target="_blank" rel="noreferrer noopener">{(() => { try { return new URL(s.url).hostname; } catch { return s.url; } })()}</a></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          <div className="row" style={{ justifyContent: "flex-end", marginTop: "1rem" }}>
            <button className="btn btn-pill" onClick={() => { setMode("menu"); setText(null); setErr(null); }}>Back</button>
          </div>
        </div>
      )}
    </BottomSheet>
  );
}
